import React, { useState } from 'react';
import styled from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';

import { authService } from '@/services/authService';
import ErrorModal from '@/components/common/ErrorModal';
import { useAuthStore } from '@/store/useAuthStore';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { Button } from '@/common/components/button/Button';
import { Input } from '@/components/ui/Input';
import Avatar from '@/common/components/avatar/Avatar';
import FactsheetLogo from '@/common/assets/logo/factsheet_logo.png';

interface LoginForm {
  loginId: string;
  password: string;
  autoSignIn: boolean;
}

const PopupLogin: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [showLoginForm, setShowLoginForm] = useState(!user);
  const [form, setForm] = useState<LoginForm>({
    loginId: '',
    password: '',
    autoSignIn: false,
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const validateForm = (): boolean => {
    if (!form.loginId.trim()) {
      setError('아이디를 입력해주세요.');
      return false;
    }
    
    if (!form.password.trim()) {
      setError('비밀번호를 입력해주세요.');
      return false;
    }
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prevForm => ({
      ...prevForm,
      [name]: value
    }));
  };

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) {
      setIsLoading(false);
      setIsErrorModalOpen(true);
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const result = await authService.login(form);
      if (result) {
        window.opener.postMessage({ success: true, user: result }, window.location.origin);
      }
    } catch (err: unknown) {
      setError((err as Error)?.message || '로그인에 실패했습니다.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLoginWithCurrentUser = async () => {
    setIsLoading(true);
    try {
      // 현재 유저 정보로 로그인 처리
      window.opener.postMessage({ success: true, user }, window.location.origin);
      window.close();
    } catch (err: any) {
      setError(err.message || '로그인에 실패했습니다.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCertify = async (certifyData: InisisAuthResult) => {
    if (!certifyData?.userDi || certifyData.userDi.length === 0) {
      setError('인증에 실패했습니다.');
      setIsErrorModalOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const result = await authService.loginWithUserDi(certifyData.userDi, certifyData.userCi, certifyData.token);
      if (result?.success) {
        window.opener.postMessage({ success: true, user: result }, window.location.origin);
      } else {
        setError(result?.message || '인증이 실패하였습니다.');
        setIsErrorModalOpen(true);
      }
    } catch (err: any) {
      setError('인증 처리 중 오류가 발생했습니다.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoggedInUser = () => (
    <LoginLayout>
      <LogoSection>
        <LogoImage src={FactsheetLogo} alt="Factsheet Logo" />
      </LogoSection>

      <FormGroup style={{ gap: 7 }}>
        <LoginLabel>계정 선택</LoginLabel>
        <LoginDescription>로그인할 계정을 선택해주세요.</LoginDescription>
      </FormGroup>

      <UserInfoSection>
        <UserProfile>
          <Avatar
            alt={user?.name || '사용자'}
            sizes="lg"
            isUser={true}
          />
          <UserDetails>
            <UserName>{user?.name}</UserName>
            <UserEmail>{user?.email}</UserEmail>
          </UserDetails>
        </UserProfile>
      </UserInfoSection>

      <ButtonGroup>
        <Button
          $variant="gradient"
          $size="md"
          onClick={handleLoginWithCurrentUser}
          disabled={isLoading}
          $isFull
        >
          {isLoading ? '로그인 중...' : '현재 계정으로 계속하기'}
        </Button>
        <Button
          $variant="outline"
          $size="md"
          onClick={() => setShowLoginForm(true)}
          $isFull
        >
          다른 계정으로 로그인
        </Button>
      </ButtonGroup>
    </LoginLayout>
  );

  const renderLoginForm = () => (
    <LoginLayout>
      <LogoSection>
        <LogoImage src={FactsheetLogo} alt="Factsheet Logo" />
      </LogoSection>

      <FormGroup style={{ gap: 7 }}>
        <LoginLabel>로그인</LoginLabel>
        <LoginDescription>계정에 로그인하여 서비스를 이용해주세요.</LoginDescription>
      </FormGroup>

      {user && (
        <BackButtonContainer>
          <BackButton onClick={() => setShowLoginForm(false)}>
            ← 돌아가기
          </BackButton>
        </BackButtonContainer>
      )}


      <FormGroup style={{ gap: 16 }}>
        <FormGroup>
          <Label className="title-medium-16" htmlFor="popup-loginId">ID</Label>
          <Input
            id="popup-loginId"
            type="text"
            placeholder="아이디를 입력해 주세요."
            name="loginId"
            value={form.loginId}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && form.loginId.trim() && form.password.trim()) {
                handleLogin();
              }
            }}
          />
        </FormGroup>

        <FormGroup>
          <Label className="title-medium-16" htmlFor="popup-password">Password</Label>
          <PasswordInputWrapper>
            <Input
              id="popup-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="비밀번호를 입력해 주세요."
              name="password"
              value={form.password}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && form.loginId.trim() && form.password.trim()) {
                  handleLogin();
                }
              }}
            />
            <PasswordToggle type="button" onClick={togglePasswordVisibility}>
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </PasswordToggle>
          </PasswordInputWrapper>
        </FormGroup>

        <Button
          $variant="gradient"
          $size="md"
          $isFull
          disabled={isLoading}
          onClick={handleLogin}
        >
          {isLoading ? '로그인 중...' : '로그인'}
        </Button>

        <SimpleDivider>
          <DividerLine />
          <DividerText>또는</DividerText>
          <DividerLine />
        </SimpleDivider>

        <SimpleAuthSection>
          <SimpleAuthGuide>
            <SimpleAuthIcon>💡</SimpleAuthIcon>
            <SimpleAuthText>
              아이디나 비밀번호가 기억나지 않으신가요?<br />
              <strong>간편 인증</strong>으로 바로 로그인하세요!
            </SimpleAuthText>
          </SimpleAuthGuide>
          <InisisAuthButton
            $variant="gradient"
            $size="md"
            $title="간편 인증으로 로그인"
            onSuccess={(data: InisisAuthResult) => {
              handleCertify(data);
            }}
          />
        </SimpleAuthSection>
      </FormGroup>

      <ErrorModal
        isOpen={isErrorModalOpen}
        message={error}
        onClose={() => setIsErrorModalOpen(false)}
      />
    </LoginLayout>
  );

  return (
    <LoginContainer>
      <LoginBox>
        {!showLoginForm && user ? renderLoggedInUser() : renderLoginForm()}
      </LoginBox>
    </LoginContainer>
  );
};

// Styled Components - aligned with Login.tsx aesthetic (popup 500x600)
const LoginContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  background-color: ${semanticColors.background.primary};
  overflow: hidden;
  padding: 16px;
`;

const LoginBox = styled.div`
  width: 100%;
  max-width: 371px;
  background-color: ${semanticColors.background.secondary};
  border-radius: 21px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: auto;
`;

const LoginLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const LogoSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const LogoImage = styled.img`
  width: 124px;
  height: auto;
  object-fit: contain;
`;

const LoginLabel = styled.h2`
  ${typography.heading.h2}
  margin: 0;
`;

const LoginDescription = styled.span`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.secondary};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  &.row {
    flex-direction: row;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${semanticColors.text.primary};
`;

const UserInfoSection = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 14px 16px;
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  word-break: break-word;
`;

const UserEmail = styled.div`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  word-break: break-word;
`;

const BackButtonContainer = styled.div`
  margin-top: -8px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  padding: 0.25rem 0;
  transition: color 0.2s;

  &:hover {
    color: ${semanticColors.text.primary};
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: ${semanticColors.text.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const SimpleDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${semanticColors.border.primary};
`;

const DividerText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const SimpleAuthSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SimpleAuthGuide = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
`;

const SimpleAuthIcon = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const SimpleAuthText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.5;
  margin: 0;

  strong {
    color: #6366f1;
    font-weight: 600;
  }
`;

export default PopupLogin;
